import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormData } from "../context/FormDataContext";
import {
  prepocitejPolozkuKvetiny,
  souctyKvetin,
  round2,
  slozTextStuh,
} from "../utils/kvetinyCalc";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #444",
  outline: "none",
  fontSize: 14,
};

const labelStyle = { fontSize: 13, fontWeight: 800, marginBottom: 6 };
const boxStyle = {
  border: "1px solid #333",
  borderRadius: 12,
  padding: 12,
  marginBottom: 12,
};
const btnStyle = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #444",
  cursor: "pointer",
  fontWeight: 900,
};

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const mkRow = () => ({
  id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
  nazev: "",
  pocet: 1,
  cenaKsSDPH: 0,
  cenaKs: 0,
  dph: 21,
  stuha1: "",
  stuha2: "",
  stuha3: "",
  stuha4: "",
  popisKytice: "",
  uctovatDalsiStuhy: true,
  priplatekZaStuhuSDPH: 70,
  poznamka: "",
});

const normalizeRow = (r = {}) => {
  const dph = Math.max(0, Math.round(toNum(r?.dph ?? 21)));
  const cenaKsBez = Math.max(0, round2(toNum(r?.cenaKs ?? 0)));
  const cenaKsSDPHFromBez = round2(cenaKsBez * (1 + dph / 100));

  const hasCenaSDPH =
    r?.cenaKsSDPH !== undefined &&
    r?.cenaKsSDPH !== null &&
    String(r?.cenaKsSDPH) !== "";

  const cenaKsSDPH = Math.max(
    0,
    round2(hasCenaSDPH ? toNum(r?.cenaKsSDPH) : cenaKsSDPHFromBez)
  );

  return prepocitejPolozkuKvetiny({
    ...r,
    id: String(r?.id || mkRow().id),
    nazev: String(r?.nazev || ""),
    pocet: Math.max(0, Math.floor(toNum(r?.pocet || 0))),
    dph,
    cenaKsSDPH,
    stuha1: String(r?.stuha1 || ""),
    stuha2: String(r?.stuha2 || ""),
    stuha3: String(r?.stuha3 || ""),
    stuha4: String(r?.stuha4 || ""),
    popisKytice: String(r?.popisKytice || ""),
    uctovatDalsiStuhy: r?.uctovatDalsiStuhy !== false,
    priplatekZaStuhuSDPH: Math.max(
      0,
      round2(toNum(r?.priplatekZaStuhuSDPH ?? 70))
    ),
    poznamka: String(r?.poznamka || ""),
  });
};

const normalizeRows = (arr) => {
  const rows = Array.isArray(arr) ? arr : [];
  return rows.length ? rows.map(normalizeRow) : [normalizeRow(mkRow())];
};

const sanitizeRowsForSave = (rows) => {
  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const n = normalizeRow(r);
      return {
        id: n.id,
        nazev: String(n.nazev || "").trim(),
        pocet: Math.max(0, Math.floor(toNum(n.pocet))),
        cenaKsSDPH: Math.max(0, round2(toNum(n.cenaKsSDPH))),
        cenaKs: Math.max(0, round2(toNum(n.cenaKs))),
        dph: Math.max(0, Math.round(toNum(n.dph))),
        stuha1: String(n.stuha1 || "").trim(),
        stuha2: String(n.stuha2 || "").trim(),
        stuha3: String(n.stuha3 || "").trim(),
        stuha4: String(n.stuha4 || "").trim(),
        popisKytice: String(n.popisKytice || "").trim(),
        uctovatDalsiStuhy: n.uctovatDalsiStuhy !== false,
        priplatekZaStuhuSDPH: Math.max(
          0,
          round2(toNum(n.priplatekZaStuhuSDPH ?? 70))
        ),
        poznamka: String(slozTextStuh(n) || n.poznamka || "").trim(),
      };
    })
    .filter(
      (r) =>
        r.nazev ||
        r.pocet ||
        r.cenaKsSDPH ||
        r.cenaKs ||
        r.stuha1 ||
        r.stuha2 ||
        r.stuha3 ||
        r.stuha4 ||
        r.popisKytice ||
        r.poznamka
    );
};

export default function VyberKvetinForm() {
  const { formData, mergeFormData, updateFormData, currentOrderId, saveOrder } = useFormData();
  const saveFormData =
    typeof updateFormData === "function" ? updateFormData : mergeFormData;

  const __orderId = String(
    currentOrderId ||
      formData?.orderId ||
      formData?.id ||
      formData?.zakazkaId ||
      ""
  ).trim();

  const initial = useMemo(() => {
    const k = formData?.kvetiny || {};
    return {
      polozky: normalizeRows(k?.polozky),
      poznamka: String(k?.poznamka || ""),
      includeFinal: Boolean(k?.includeFinal),
      includeDocs: Boolean(k?.includeDocs),
    };
  }, [formData?.kvetiny]);

  const [polozky, setPolozky] = useState(initial.polozky);
  const [poznamka, setPoznamka] = useState(initial.poznamka);
  const [includeFinal, setIncludeFinal] = useState(initial.includeFinal);
  const [includeDocs, setIncludeDocs] = useState(initial.includeDocs);
  const [saved, setSaved] = useState(false);

  const __kvetinyTouchedRef = useRef(false);
  const __kvetinyHydratingRef = useRef(false);
  const __lastKvetinyAutoSaveRef = useRef("");

  const totals = useMemo(() => {
    const s = souctyKvetin(polozky);
    return {
      bez: Math.round(s.bezDPH),
      dph: Math.round(s.dph),
      sDph: Math.round(s.sDPH),
    };
  }, [polozky]);

  const __kvetinySig = (rows, poz, incFinal, incDocs) => {
    try {
      const cleanRows = sanitizeRowsForSave(rows)
        .map((r) => ({
          ...r,
          id: String(r.id || ""),
        }))
        .sort((a, b) => (a.id || "").localeCompare(b.id || ""));

      return JSON.stringify({
        polozky: cleanRows,
        poznamka: String(poz || "").trim(),
        includeFinal: Boolean(incFinal),
        includeDocs: Boolean(incDocs),
      });
    } catch {
      return "";
    }
  };

  const __knihaSaveNow = async () => {
    try {
      if (typeof saveOrder !== "function") {
        throw new Error("Chybí saveOrder() v kontextu.");
      }

      const out = await saveOrder();
      console.log("🌸 kvetiny saveOrder ok:", out);
      return out || { ok: true };
    } catch (e) {
      console.warn(
        "🌸 kvetiny saveOrder failed:",
        String(e && e.message ? e.message : e)
      );
      return { ok: false, error: String(e && e.message ? e.message : e) };
    }
  };

  const setRow = (id, patch) => {
    setPolozky((prev) =>
      prev.map((r) => (r.id === id ? normalizeRow({ ...r, ...patch }) : r))
    );
    setSaved(false);
    __kvetinyTouchedRef.current = true;
  };

  const addRow = () => {
    setPolozky((prev) => [...prev, normalizeRow(mkRow())]);
    setSaved(false);
    __kvetinyTouchedRef.current = true;
  };

  const delRow = (id) => {
    setPolozky((prev) => {
      const next = prev.filter((r) => r.id !== id);
      return next.length ? next.map(normalizeRow) : [normalizeRow(mkRow())];
    });
    setSaved(false);
    __kvetinyTouchedRef.current = true;
  };

  const handleSave = async () => {
    if (!__orderId) {
      try {
        alert("Zakázka ještě není načtená (orderId je prázdné). Počkej 1–2 s a zkus znovu.");
      } catch {}
      return;
    }

    const clean = sanitizeRowsForSave(polozky);
    const cleanPoznamka = String(poznamka || "").trim();
    const payloadKvetiny = {
      polozky: clean,
      poznamka: cleanPoznamka,
      soucty: totals,
      includeFinal: Boolean(includeFinal),
      includeDocs: Boolean(includeDocs),
    };

    try {
      saveFormData(
        {
          kvetiny: payloadKvetiny,
        },
        "kvetiny:manual"
      );
    } catch {}

    try {
      const id = String(
        __orderId ||
          formData?.id ||
          formData?.orderId ||
          formData?.currentOrderId ||
          ""
      ).trim();

      if (id) {
        const fd2 = {
          ...(formData || {}),
          id,
          orderId: id,
          currentOrderId: id,
          zakazkaId: id,
          kvetiny: payloadKvetiny,
        };
        await __knihaSaveNow(fd2);
      } else {
        console.warn("🌸 kvetiny manual save: no __orderId");
      }
    } catch {}

    try {
      __lastKvetinyAutoSaveRef.current = __kvetinySig(
        clean,
        cleanPoznamka,
        includeFinal,
        includeDocs
      );
    } catch {}

    setSaved(true);
    __kvetinyTouchedRef.current = false;
  };

  useEffect(() => {
    const k = formData?.kvetiny || {};
    const inPolozky = Array.isArray(k?.polozky) ? k.polozky : [];
    const inPoz = String(k?.poznamka || "");
    const inIncFinal = Boolean(k?.includeFinal);
    const inIncDocs = Boolean(k?.includeDocs);

    const incomingSig = __kvetinySig(inPolozky, inPoz, inIncFinal, inIncDocs);
    const localSig = __kvetinySig(polozky, poznamka, includeFinal, includeDocs);

    if (!incomingSig || incomingSig === localSig) return;

    __kvetinyHydratingRef.current = true;
    __lastKvetinyAutoSaveRef.current = incomingSig;

    setPolozky(normalizeRows(inPolozky));
    setPoznamka(inPoz);
    setIncludeFinal(inIncFinal);
    setIncludeDocs(inIncDocs);

    setTimeout(() => {
      __kvetinyHydratingRef.current = false;
    }, 0);
  }, [__orderId, formData?.kvetiny]);

  useEffect(() => {
    if (!__orderId) return;
    if (!__kvetinyTouchedRef.current) return;
    if (__kvetinyHydratingRef.current) return;

    const nextSig = __kvetinySig(polozky, poznamka, includeFinal, includeDocs);
    if (!nextSig || nextSig === __lastKvetinyAutoSaveRef.current) return;

    const cur = formData?.kvetiny || {};
    const curSig = __kvetinySig(
      cur?.polozky,
      cur?.poznamka,
      cur?.includeFinal,
      cur?.includeDocs
    );

    if (nextSig === curSig) {
      __lastKvetinyAutoSaveRef.current = nextSig;
      return;
    }

    const t = setTimeout(() => {
      const nextSig2 = __kvetinySig(polozky, poznamka, includeFinal, includeDocs);
      if (!nextSig2 || nextSig2 === __lastKvetinyAutoSaveRef.current) return;

      const cur2 = formData?.kvetiny || {};
      const curSig2 = __kvetinySig(
        cur2?.polozky,
        cur2?.poznamka,
        cur2?.includeFinal,
        cur2?.includeDocs
      );

      if (nextSig2 === curSig2) {
        __lastKvetinyAutoSaveRef.current = nextSig2;
        __kvetinyTouchedRef.current = false;
        return;
      }

      __lastKvetinyAutoSaveRef.current = nextSig2;

      try {
        mergeFormData(
          {
            kvetiny: {
              polozky: sanitizeRowsForSave(polozky),
              poznamka: String(poznamka || "").trim(),
              soucty: totals,
              includeFinal: Boolean(includeFinal),
              includeDocs: Boolean(includeDocs),
            },
          },
          "kvetiny:auto"
        );
      } catch {}
    }, 350);

    return () => clearTimeout(t);
  }, [__orderId, polozky, poznamka, includeFinal, includeDocs, totals, formData?.kvetiny, mergeFormData]);

  return (
    <div>
      <h2 style={{ margin: "8px 0 14px" }}>🌸 Květiny</h2>

      <div style={boxStyle}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <button type="button" style={btnStyle} onClick={addRow}>
            + Přidat položku
          </button>
          <button type="button" style={btnStyle} onClick={handleSave}>
            Uložit květiny
          </button>
          {saved ? (
            <span style={{ fontWeight: 900, color: "limegreen" }}>✅ Uloženo</span>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
          <label
            style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 900 }}
          >
            <input
              type="checkbox"
              checked={includeDocs}
              onChange={(e) => {
                setIncludeDocs(e.target.checked);
                setSaved(false);
                __kvetinyTouchedRef.current = true;
              }}
            />
            Zahrnout do dokumentů
          </label>

          <label
            style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 900 }}
          >
            <input
              type="checkbox"
              checked={includeFinal}
              onChange={(e) => {
                setIncludeFinal(e.target.checked);
                setSaved(false);
                __kvetinyTouchedRef.current = true;
              }}
            />
            Zahrnout do finální objednávky
          </label>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 1320, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #333" }}>
                  Název
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 90,
                  }}
                >
                  Počet
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 140,
                  }}
                >
                  Cena/ks s DPH
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 140,
                  }}
                >
                  Cena/ks bez DPH
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 90,
                  }}
                >
                  DPH %
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    minWidth: 380,
                  }}
                >
                  Stuhy
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 150,
                  }}
                >
                  Příplatek stuhy
                </th>
                <th
                  style={{
                    textAlign: "right",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 150,
                  }}
                >
                  Celkem řádek
                </th>
                <th
                  style={{
                    textAlign: "center",
                    padding: 6,
                    borderBottom: "1px solid #333",
                    width: 90,
                  }}
                >
                  Akce
                </th>
              </tr>
            </thead>

            <tbody>
              {polozky.map((r) => (
                <React.Fragment key={r.id}>
                  <tr style={{ borderBottom: "1px solid #222", verticalAlign: "top" }}>
                    <td style={{ padding: 6 }}>
                      <input
                        style={inputStyle}
                        value={r.nazev}
                        placeholder="např. Věnec – bílý, stuha…"
                        onChange={(e) => setRow(r.id, { nazev: e.target.value })}
                      />
                    </td>

                    <td style={{ padding: 6 }}>
                      <input
                        style={inputStyle}
                        value={r.pocet}
                        inputMode="numeric"
                        onChange={(e) => setRow(r.id, { pocet: e.target.value })}
                      />
                    </td>

                    <td style={{ padding: 6 }}>
                      <input
                        style={inputStyle}
                        value={r.cenaKsSDPH ?? ""}
                        inputMode="decimal"
                        onChange={(e) => setRow(r.id, { cenaKsSDPH: e.target.value })}
                      />
                    </td>

                    <td style={{ padding: 6 }}>
                      <input
                        style={{ ...inputStyle, background: "#1b1b1b", color: "#ddd" }}
                        value={toNum(r.cenaKs).toFixed(2)}
                        readOnly
                      />
                    </td>

                    <td style={{ padding: 6 }}>
                      <input
                        style={inputStyle}
                        value={r.dph}
                        inputMode="numeric"
                        onChange={(e) => setRow(r.id, { dph: e.target.value })}
                      />
                    </td>

                    <td style={{ padding: 6 }}>
                      <div style={{ display: "grid", gap: 6 }}>
                        <input
                          style={inputStyle}
                          value={r.stuha1 || ""}
                          placeholder="Stuha 1"
                          onChange={(e) => setRow(r.id, { stuha1: e.target.value })}
                        />
                        <input
                          style={inputStyle}
                          value={r.stuha2 || ""}
                          placeholder="Stuha 2"
                          onChange={(e) => setRow(r.id, { stuha2: e.target.value })}
                        />
                        <input
                          style={inputStyle}
                          value={r.stuha3 || ""}
                          placeholder="Stuha 3 (volitelné)"
                          onChange={(e) => setRow(r.id, { stuha3: e.target.value })}
                        />
                        <input
                          style={inputStyle}
                          value={r.stuha4 || ""}
                          placeholder="Stuha 4 (volitelné)"
                          onChange={(e) => setRow(r.id, { stuha4: e.target.value })}
                        />

                        <label
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={r.uctovatDalsiStuhy !== false}
                            onChange={(e) =>
                              setRow(r.id, { uctovatDalsiStuhy: e.target.checked })
                            }
                          />
                          Účtovat 3. a další stuhu
                        </label>

                        <div style={{ display: "grid", gap: 6 }}>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>
                            Aktivní stuhy: {r.pocetStuh || 0} • Nad 2:{" "}
                            {r.pocetStuhNavic || 0} •{" "}
                            {Math.round(toNum(r.priplatekZaStuhuSDPH || 70))} Kč / ks
                          </div>

                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                marginBottom: 4,
                              }}
                            >
                              Cena za 3. a další stuhu (Kč s DPH)
                            </div>
                            <input
                              style={inputStyle}
                              value={r.priplatekZaStuhuSDPH ?? ""}
                              inputMode="decimal"
                              onChange={(e) =>
                                setRow(r.id, {
                                  priplatekZaStuhuSDPH: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: 6, textAlign: "right", fontWeight: 800 }}>
                      {Math.round(toNum(r.priplatekStuhySDPH || 0))} Kč
                    </td>

                    <td style={{ padding: 6, textAlign: "right", fontWeight: 900 }}>
                      {Math.round(toNum(r.cenaCelkemSDPH || 0))} Kč
                    </td>

                    <td style={{ padding: 6, textAlign: "center" }}>
                      <button type="button" style={btnStyle} onClick={() => delRow(r.id)}>
                        Smazat
                      </button>
                    </td>
                  </tr>

                  <tr style={{ borderBottom: "1px solid #222" }}>
                    <td colSpan={5} style={{ padding: "0 6px 10px 6px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                        Popis kytice / vazby
                      </div>
                      <textarea
                        style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
                        value={r.popisKytice || ""}
                        placeholder="např. 8 červených růží, 3 bílé lilie, více zeleně, bílo-červená vazba, bez karafiátů, kulatá vazba..."
                        onChange={(e) =>
                          setRow(r.id, { popisKytice: e.target.value })
                        }
                      />
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12 }}>
          <div>
            <span style={labelStyle}>Součet bez DPH:</span> <b>{totals.bez} Kč</b>
          </div>
          <div>
            <span style={labelStyle}>DPH:</span> <b>{totals.dph} Kč</b>
          </div>
          <div>
            <span style={labelStyle}>Celkem s DPH:</span> <b>{totals.sDph} Kč</b>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={labelStyle}>Poznámka ke květinám</div>
          <textarea
            style={{ ...inputStyle, minHeight: 90 }}
            value={poznamka}
            onChange={(e) => {
              setPoznamka(e.target.value);
              setSaved(false);
              __kvetinyTouchedRef.current = true;
            }}
            placeholder="např. donést na obřad, typ stuhy, domluva s květinářkou…"
          />
        </div>
      </div>

      <div style={boxStyle}>
        <div style={labelStyle}>Debug (ukládá se do formData.kvetiny)</div>
        <pre style={{ whiteSpace: "pre-wrap", opacity: 0.85 }}>
          {JSON.stringify(
            {
              polozky,
              poznamka,
              totals,
              includeFinal,
              includeDocs,
            },
            null,
            2
          )}
        </pre>
      </div>
    </div>
  );
}
